import { useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';

import { ProviderFieldsForm } from '../../../PaymentProviders/components';
import {
  useGetPaymentProvider,
  useGetRestaurantPaymentConfigs,
} from '../../../PaymentProviders/services';
import { TPaymentTerminalProvider, TFieldSchema } from '../../../PaymentProviders/schemas/payment-provider-schema';

type RestaurantConfigDialogProps = {
  open: boolean;
  mode: 'create' | 'edit';
  restaurantId: string;
  providerId: string | null;
  availableProviders: TPaymentTerminalProvider[];
  onClose: () => void;
  onSave: (providerId: string, clientFields: Record<string, string>) => void;
  isSaving?: boolean;
};

const RestaurantConfigDialog = ({
  open,
  mode,
  restaurantId,
  providerId,
  availableProviders,
  onClose,
  onSave,
  isSaving,
}: RestaurantConfigDialogProps) => {
  const [selectedProviderId, setSelectedProviderId] = useState<string>('');
  const [values, setValues] = useState<Record<string, string>>({});

  const targetProviderId = mode === 'edit' ? providerId || '' : selectedProviderId;

  const { data: providerCatalog, isLoading: catalogLoading, refetch: refetchProvider } =
    useGetPaymentProvider(targetProviderId);

  const { data: configsData, isLoading: configsLoading, refetch: refetchConfigs } =
    useGetRestaurantPaymentConfigs(mode === 'edit' ? restaurantId : '');

  const existingConfig = useMemo(() => {
    if (mode !== 'edit' || !providerId || !configsData) return null;
    return configsData.rows.find((row) => row.providerId === providerId) || null;
  }, [configsData, mode, providerId]);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && providerId) {
      refetchProvider();
      refetchConfigs();
    }
  }, [open, mode, providerId, refetchProvider, refetchConfigs]);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && existingConfig) {
      setValues({ ...(existingConfig.clientFields || {}) });
    } else if (mode === 'create') {
      setSelectedProviderId(availableProviders[0]?.providerId || '');
      setValues({});
    }
  }, [open, mode, existingConfig, availableProviders]);

  const allCatalogFields: TFieldSchema[] = useMemo(
    () => providerCatalog?.schema?.fields || [],
    [providerCatalog],
  );
  const clientFieldSchema = useMemo(() => {
    if (allCatalogFields.length > 0) {
      return allCatalogFields.filter((f) => f.scope === 'client');
    }
    return existingConfig?.schema?.fields || [];
  }, [allCatalogFields, existingConfig]);

  const selectedProvider = useMemo(() => {
    if (mode === 'edit') {
      if (!existingConfig && !providerCatalog) return null;
      return {
        providerId: (existingConfig?.providerId || providerCatalog?.providerId) as string,
        displayName: existingConfig?.displayName || providerCatalog?.displayName,
        encryptedKeys: existingConfig?.clientFieldsEncryptedKeys || [],
      };
    }
    const p = availableProviders.find((q) => q.providerId === selectedProviderId);
    if (!p) return null;
    return {
      providerId: p.providerId,
      displayName: p.displayName,
      encryptedKeys: [] as string[],
    };
  }, [
    mode,
    existingConfig,
    providerCatalog,
    availableProviders,
    selectedProviderId,
  ]);

  const handleSave = () => {
    if (!selectedProvider) return;
    onSave(selectedProvider.providerId, values);
  };

  const noClientFields = selectedProvider && clientFieldSchema.length === 0;

  const isFetching =
    (mode === 'edit' && (catalogLoading || configsLoading)) ||
    (mode === 'create' && !!targetProviderId && catalogLoading);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={styles.title}>
        {mode === 'edit'
          ? `Edit ${selectedProvider?.displayName || selectedProvider?.providerId || 'Provider'} Config`
          : 'Add Provider Config'}
        <Typography sx={styles.subtitle}>
          Restaurant-specific client credentials. Secrets are encrypted on save.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={styles.content}>
          {isFetching ? (
            <Box sx={styles.loaderBox}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              {mode === 'create' && (
                <Box mb={2}>
                  {availableProviders.length === 0 ? (
                    <Alert severity="info">
                      All providers are already configured for this restaurant. Edit an existing
                      one or delete it first.
                    </Alert>
                  ) : (
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Provider"
                      value={selectedProviderId}
                      onChange={(e) => {
                        setSelectedProviderId(e.target.value);
                        setValues({});
                      }}
                      disabled={isSaving}
                    >
                      {availableProviders.map((p) => (
                        <MenuItem key={p.providerId} value={p.providerId}>
                          {p.displayName || p.providerId}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </Box>
              )}

              {selectedProvider && !noClientFields && (
                <ProviderFieldsForm
                  schema={clientFieldSchema}
                  values={values}
                  encryptedKeys={selectedProvider.encryptedKeys}
                  onChange={setValues}
                  disabled={isSaving}
                />
              )}

              {selectedProvider && noClientFields && (
                <Alert severity="warning">
                  This provider has no client-scope fields defined yet.
                </Alert>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={styles.actions}>
        <Button sx={styles.cancelBtn} onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          sx={styles.saveBtn}
          onClick={handleSave}
          disabled={
            isSaving ||
            isFetching ||
            !selectedProvider ||
            (mode === 'create' && availableProviders.length === 0)
          }
        >
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RestaurantConfigDialog;

const styles = {
  title: {
    fontFamily: 'GilroyBold',
    fontSize: 18,
  },
  subtitle: {
    fontFamily: 'GilroyRegular',
    fontSize: 13,
    color: '#666',
    mt: 0.5,
  },
  content: {
    pt: 1,
    minHeight: 160,
  },
  loaderBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 120,
  },
  actions: {
    px: 3,
    py: 2,
  },
  cancelBtn: {
    textTransform: 'capitalize' as const,
    color: '#000',
    fontFamily: 'GilroySemiBold',
    border: '1px solid #000',
    borderRadius: '8px',
    px: 2.5,
  },
  saveBtn: {
    textTransform: 'capitalize' as const,
    backgroundColor: '#000',
    color: '#FFF',
    fontFamily: 'GilroySemiBold',
    borderRadius: '8px',
    px: 2.5,
    '&:hover': { backgroundColor: '#333' },
    '&:disabled': { opacity: 0.5, color: '#FFF' },
  },
};
